import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import useNavigationWarning from '../hooks/useNavigationWarning';
import { logActivity } from '../utils/sessionManager';
import './PoseSelect.css';
import ConfidentReadyStance from '../assets/heroPose/ConfidentReadyStance.png';
import DynamicFlightLeap from '../assets/heroPose/DynamicFlightLeap.png';
import PowerMove from '../assets/heroPose/PowerMove.png';
import LandingPose from '../assets/heroPose/LandingPose.png';
import SideCombatProfile from '../assets/heroPose/SideCombatProfile.png';

const PoseSelect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { superhero, userPhoto, email } = location.state || {};

  // Add navigation warning
  const { NavigationWarningModal } = useNavigationWarning(true);

  // Log page visit
  useEffect(() => {
    logActivity('pose_select_visited', { 
      superhero: superhero?.name,
      has_photo: !!userPhoto,
      has_email: !!email
    });
  }, [superhero, userPhoto, email]);

  // List of poses to choose from
  const poses = [
    { id: 1, name: 'Dynamic Flight Leap', image: DynamicFlightLeap },
    { id: 2, name: 'Side Combat Profile', image: SideCombatProfile },
    { id: 3, name: 'Signature Power Move', image: PowerMove },
    { id: 4, name: 'Landing Pose', image: LandingPose },
  ];

  const handleSelectPose = (pose) => {
    logActivity('pose_selected', {
      pose: pose.name,
      superhero: superhero?.name
    });

    navigate('/generate', {
      state: {
        superhero: superhero,
        userPhoto: userPhoto,
        pose: pose,
        email
      }
    });
  };



  const handleBack = () => {
    logActivity('navigation_back_from_pose_select');
    navigate('/capture-photo', { state: { superhero, email } });
  };

  const handleHomeClick = () => {
    logActivity('navigation_home_from_pose_select');
    navigate('/', { replace: true });
  };

  return (
    <div className="pose-select">
      <div className="superhero-background"></div>

      {/* Render the navigation warning modal */}
      {NavigationWarningModal}

      <motion.button
        className="back-button"
        onClick={handleBack}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        ← BACK
      </motion.button>

      <motion.button
        className="home-button"
        onClick={handleHomeClick}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        HOME
      </motion.button>

      <div className="content-container">
        <motion.div
          className="hero-info"
          initial={{ y: -30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <p className="selected-hero-label">Selected Hero:</p>
          <h2 className="selected-hero-name">{superhero?.name || 'Unknown Hero'}</h2>
        </motion.div>

        <motion.h1 
          className="page-title"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          CHOOSE YOUR POSE
        </motion.h1>

        <div className="poses-grid">
          {poses.map((pose, index) => (
            <motion.div
              key={pose.id}
              className="pose-card"
              style={{ backgroundImage: `url(${pose.image})` }}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: index * 0.1 + 0.3, duration: 0.4 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleSelectPose(pose)}
            >
              <h3 className="pose-name">{pose.name}</h3>
            </motion.div>
          ))}
        </div>


      </div>
    </div>
  );
};

export default PoseSelect;
